import { useEffect } from "react";

export interface ScrollToHashProps {
  /** Hash value (e.g., "#services"). If omitted, falls back to window.location.hash + listeners. */
  hash?: string;
  /** Optional offset in pixels to account for sticky headers */
  offset?: number;
}

/**
 * Scrolls to an element matching the provided hash. When `hash` is provided, it reacts to prop changes.
 * When omitted, it listens to hashchange/popstate events (non-router use).
 */
export function ScrollToHash({ hash, offset = 0 }: ScrollToHashProps) {
  useEffect(() => {
    const scrollTo = (targetHash: string | null) => {
      if (!targetHash) return;
      const id = targetHash.replace("#", "");
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: "smooth" });
          return;
        }
        if (attempts < 10) {
          attempts += 1;
          window.setTimeout(tryScroll, 80);
        }
      };
      window.setTimeout(tryScroll, 0);
    };

    // If hash prop is provided, just react to it
    if (hash !== undefined) {
      scrollTo(hash || null);
      return;
    }

    // Fallback: listen to hashchange/popstate when no hash prop is passed
    const handler = () => scrollTo(window.location.hash || null);
    handler(); // on mount
    window.addEventListener("hashchange", handler);
    window.addEventListener("popstate", handler);
    return () => {
      window.removeEventListener("hashchange", handler);
      window.removeEventListener("popstate", handler);
    };
  }, [hash, offset]);

  return null;
}
